import { useEffect, useState } from "react";
import { SyncMap, Client as TwilioSyncClient } from "twilio-sync";

import TwilioSyncMapTypes from "./definitions";

const useTwilioSyncMap = <
  CustomData extends Record<string, unknown> = Record<string, unknown>
>({
  onItemAdded,
  onItemRemoved,
  onItemUpdated,
  onInitialize,
  onError,
  accessToken,
  syncMapId,
}: TwilioSyncMapTypes.UseTwilioSyncMapArgs<CustomData>) => {
  const [syncMap, setSyncMap] = useState<SyncMap | null>(null);

  useEffect(() => {
    if (!accessToken || !syncMapId) {
      throw new Error("accessToken and syncMapId are required");
    }

    const syncClient = new TwilioSyncClient(accessToken);

    syncClient
      .map(syncMapId)
      .then((map) => {
        setSyncMap(map);

        /**
         * @see https://media.twiliocdn.com/sdk/js/sync/releases/3.2.2/docs/classes/SyncMap.html#itemAdded
         */
        map.on(
          "itemAdded",
          (event: TwilioSyncMapTypes.SyncMapItemAddedEvent<CustomData>) => {
            if (onItemAdded) {
              onItemAdded(event);
            }
          }
        );

        /**
         * @see https://media.twiliocdn.com/sdk/js/sync/releases/3.2.2/docs/classes/SyncMap.html#itemRemoved
         */
        map.on(
          "itemRemoved",
          (event: TwilioSyncMapTypes.SyncMapItemRemovedEvent<CustomData>) => {
            if (onItemRemoved) {
              onItemRemoved(event);
            }
          }
        );

        /**
         * @see https://media.twiliocdn.com/sdk/js/sync/releases/3.2.2/docs/classes/SyncMap.html#itemUpdated
         */
        map.on(
          "itemUpdated",
          (event: TwilioSyncMapTypes.SyncMapItemUpdatedEvent<CustomData>) => {
            if (onItemUpdated) {
              onItemUpdated(event);
            }
          }
        );

        if (onInitialize) {
          onInitialize(map);
        }
      })
      .catch(onError);

    return () => {
      if (syncMap) {
        syncMap.close();
        setSyncMap(null);
      }
      syncClient && syncClient.shutdown();
    };
  }, []);

  return {
    syncMap,
  };
};

export default useTwilioSyncMap;
